<template>
  <!-- UQ detail "Documents" (Android IndependentQuoteDetailFragment "Documents" → AditionalDocumentUploadFragment):
       every document / photo of the quote as the S&R gallery (PhotoGallery), upload + delete unless read-only -->
  <div class="shadow-[0px_0px_15px_-2px_#ccc] rounded-[20px] p-[15px] bg-white font-roboto">
    <div class="flex items-center gap-1 pb-[15px]">
      <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
      <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
      <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Documents</p>
    </div>
    <p class="!mb-[15px] text-[14px] text-[#333333]">
      <span class="text-[#F67280]">Note:</span> Please avoid uploading any financial documents
    </p>
    <div v-if="!readOnly" class="flex flex-wrap items-center gap-[10px] mb-[20px]">
      <button type="button"
        class="flex items-center justify-center gap-[8px] w-[170px] h-[36px] rounded-[5px] border-1 border-[#7695FF] bg-[#7695FF] text-[14px] text-white"
        @click="$refs.fileInput.click()">
        Upload documents
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 8.5a4 4 0 0 1-.5 7.97M12 12v7m0-7-2.5 2.5M12 12l2.5 2.5"
            stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
      <span class="text-[12px] text-[#F67280]">(Max file size to upload is 10 MB)</span>
    </div>
    <div v-if="!galleryFiles.length"
      class="flex flex-col items-center justify-center min-h-[150px] px-[15px] rounded-[10px] border-1 border-dashed border-[#CCCCCC] bg-[#EFEFEF80] text-center">
      <p class="m-0 text-[14px] leading-[20px] text-[#000000]">No documents to display</p>
    </div>
    <PhotoGallery v-else :files="galleryFiles" :deletable="!readOnly" alt-text="Document" @remove="remove" />
    <input ref="fileInput" type="file" multiple class="hidden" accept="image/*,application/pdf" @change="onFiles">
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import PhotoGallery from "../gallery/PhotoGallery.vue";
import { uqApi } from "@/services/uqApi";

const toast = useToast();
const MAX_SIZE = 10 * 1024 * 1024; // backend limit (OtherDocumentUploadRequest: max 10MB)

export default {
  components: { PhotoGallery },
  props: {
    quoteId: { type: [Number, String], required: true },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return { documents: [], uploading: [] };
  },
  computed: {
    // PhotoGallery shape; files still uploading show a spinner tile
    galleryFiles() {
      return this.documents
        .map(d => ({ id: d.id, url: d.url, title: d.title, mime: this.isPdf(d) ? "pdf" : "image" }))
        .concat(this.uploading);
    },
  },
  watch: {
    quoteId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      const res = await uqApi.documents(this.quoteId);
      if (res.ok && Array.isArray(res.body)) this.documents = res.body;
    },
    isPdf(doc) {
      return (doc.mime || "").includes("pdf") || /\.pdf(\?|$)/i.test(doc.url || "");
    },
    async onFiles(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = ""; // same file can be picked again
      if (!files.length) return;
      if (files.some(f => f.size > MAX_SIZE)) return toast.error("Selected media size must not exceed 10 MB.");
      // each file uploads on its own, titled with its file name (Android does the same)
      for (const file of files) {
        const tile = { id: "up-" + Math.random(), url: "", title: file.name, mime: "image", isLoading: true };
        this.uploading.push(tile);
        const res = await uqApi.uploadDocument(this.quoteId, file, file.name.replace(/\.[^.]+$/, ""));
        // by id: the array holds a reactive proxy, never === the raw tile
        this.uploading = this.uploading.filter(t => t.id !== tile.id);
        if (!res.ok) {
          toast.error(res.message || `Unable to upload ${file.name}.`);
          continue;
        }
        this.documents.push(res.body);
        toast.success(res.message || "Uploaded Successfully");
      }
    },
    remove(file) {
      this.$dialog.show({
        title: "Delete Document",
        text: "Are you sure you want to delete?",
        buttonText: "Delete",
        onConfirm: async () => {
          const res = await uqApi.removeDocument(this.quoteId, file.id);
          if (!res.ok) return toast.error(res.message || "Unable to delete the document.");
          this.documents = this.documents.filter(d => d.id !== file.id);
          toast.success(res.message || "Deleted Successfully");
        },
      });
    },
  },
};
</script>
